import { map, catchError } from 'rxjs/operators'
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http'
import { Observable, of } from 'rxjs'
import { Injectable } from '@angular/core'
import { AppConfig } from '../app.config'
import { Store } from '@ngrx/store'
import { AppState, FlightNetworkCenterState, UserState, getReleaseInfo } from '../@store'
import { SentryContextService } from './sentry-context.service'
import { uniqueId } from './unique-id'
import { UserPermissionsObject } from '../@store/reducers/user.reducer'

export interface AuthResult {
  user: UserState
  permissions: UserPermissionsObject
}

interface BackendVersions {
  backend_version?: string
  release_version?: string
}

/**
 * Handles all missions management http calls
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private http: HttpClient,
    private appConfig: AppConfig,
    private store: Store<AppState>,
    private sentryContextService: SentryContextService,
  ) {}

  login(credentials: { email: string; password: string }): Observable<void> {
    const url = this.appConfig.config.server_api.users.login
    return this.http.post<void>(url, credentials)
  }

  /**
   * perform the logout on the backend and ensure that the token is added to the InvalidToken list, this is to
   * prevent replay attacks since localStorage.getItem is transparent to any user
   */
  logout(): Observable<object> {
    const url = this.appConfig.config.server_api.users.logout
    return this.http.post(url, {})
  }

  signup(inputFields: SignupFormData): Observable<object> {
    const url = this.appConfig.config.server_api.users.signup
    return this.http.post(url, inputFields)
  }

  checkExistingEmail(term: string): Observable<object> {
    const url = this.appConfig.config.server_api.users.email_exists
    const params = { email: term }
    return this.http.post(url, params)
  }

  setUserAvatar(file: File): Observable<any> {
    const formData = new FormData()
    /**
     * Empty headers are important, because if we are loading file,
     * optimal header will be set by default, no need to manual content-type=multipart/form-data
     */
    const headers = new HttpHeaders()
    const url = this.appConfig.config.server_api.users.set_user_profile_picture
    formData.append('profile_picture', file)
    return this.http.post(url, formData, { headers })
  }

  getCurrentCenter(): Observable<FlightNetworkCenterState> {
    const url = this.appConfig.config.server_api.centers.get + '1/'
    return this.http.get<FlightNetworkCenterState>(url)
  }

  autheticate(): Observable<AuthResult> {
    const url = this.appConfig.config.server_api.users.authenticate
    return this.http.post(url, {}).pipe(
      map((result) => {
        const user = result['user_data']
        const permissions = result['permissions']
        if (user) {
          this.sentryContextService.setUser(user)
        }
        return { permissions, user }
      }),
      catchError((error: Error) => {
        console.error('Authentication error', { error, url, this: this })
        return of({
          permissions: null,
          user: null,
        })
      }),
    )
  }

  getBackendVersionInfo(): Observable<BackendVersions> {
    const url = this.appConfig.api_urls.version
    return this.http.get(url).pipe(catchError(() => of({})))
  }

  getFrontendVersionInfo(): Observable<string> {
    const url = `${this.appConfig.release_info_file_url}?v=${uniqueId()}`
    return this.http.get(url, { responseType: 'text' }).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 404) {
          console.error('No version.txt file found for current deployment.')
        }
        /**
         * Return current version to prevent reloading when no file on server.
         * This situation can appear when current build in process
         * and ./dist directory is cleared from previous build files
         */
        return this.store.select(getReleaseInfo).pipe(map((releaseInfo) => releaseInfo.frontend_version))
      }),
    )
  }
}

export interface SignupResult {
  message?: string
  errors?: number[]
}

export interface EmailCheckResult {
  value: number
  message?: string
}

export enum SignupErrorStatus {
  EmailRegistered = 1,
  CodeInvalid = 2,
  CodeRegistered = 3,
}

export enum EmailCheckStatus {
  EmailExists = 1,
  EmailDoesNotExists = 0,
}

export interface SignupFormData {
  email: string
  password: string
  first_name: string
  last_name: string
  confirm_password: string
}
